import { GenericTable } from "@shared/components/GenericTable";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { Tabs } from "@shared/ui/tabs";
import {
  Info,
  Upload,
  X,
  Play,
  FileSpreadsheet,
  CheckCircle2,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";

import {
  format,
  subDays,
  startOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import {
  useBanks,
  usePaymentChannelChoices,
  useStartReconciliation,
  useReconciliationSystemReceipts,
  useUploadBankStatement,
} from "@features/cx/paymentsv2/hooks";
import { TableColumn } from "@shared/types/table";
import { Progress } from "@shared/ui/progress";

interface StartReconciliationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StartReconciliationModal: React.FC<
  StartReconciliationModalProps
> = ({ isOpen, onClose }) => {
  const currency = useCurrency();
  const [activeRange, setActiveRange] = useState("Today");
  const remoteUtilityId = useRemoteUtilityId();
  const { data: banksResponse } = useBanks(Number(remoteUtilityId), isOpen);

  const sourceBankOptions = React.useMemo(() => {
    if (!banksResponse) return [];
    return banksResponse?.results.map((bank: any) => ({
      label: `${bank.bankName || "N/A"} — ${bank.accountTypeDisplay || "N/A"}(Routing: ${bank.routingNumber || "N/A"})`,
      value: bank.id,
    }));
  }, [banksResponse]);

  const { data: paymentChannel } = usePaymentChannelChoices(
    Number(remoteUtilityId),
  );
  const { mutate: startReconciliation, isPending: isStartingReconciliation } =
    useStartReconciliation();

  const today = new Date();
  const form = useForm({
    defaultValues: {
      fromDate: format(today, "yyyy-MM-dd"),
      toDate: format(today, "yyyy-MM-dd"),
      sourceBank: "",
    },
  });

  const fromDate = form.watch("fromDate");
  const toDate = form.watch("toDate");

  const { data: systemReceiptsData } = useReconciliationSystemReceipts({
    remoteUtilityId: Number(remoteUtilityId),
    periodFrom:
      typeof fromDate === "string"
        ? fromDate
        : format(new Date(fromDate || today), "yyyy-MM-dd"),
    periodTo:
      typeof toDate === "string"
        ? toDate
        : format(new Date(toDate || today), "yyyy-MM-dd"),
  });

  const uploadBankStatement = useUploadBankStatement();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const tableData = React.useMemo(() => {
    const channelTable = systemReceiptsData?.result?.channelTable;
    const dataArray = Array.isArray(channelTable)
      ? channelTable
      : systemReceiptsData?.results ||
        systemReceiptsData?.result ||
        systemReceiptsData;

    if (Array.isArray(dataArray) && dataArray.length > 0) {
      return dataArray.map((item: any) => ({
        id: (item.paymentMode || item.code || item.channel || "")
          .toString()
          .toLowerCase()
          .trim(),
        channel: item.paymentMode || item.channelDisplay || item.channel || "",
        systemReceipts: Number(
          item.totalAmount ||
            item.systemReceipts ||
            item.systemReceiptAmount ||
            item.amount ||
            0,
        ),
      }));
    }
    return [];
  }, [systemReceiptsData]);

  React.useEffect(() => {
    const channelTable = systemReceiptsData?.result?.channelTable;
    if (Array.isArray(channelTable) && channelTable.length > 0) {
      const initialCredits: Record<string, number> = {};
      channelTable.forEach((item: any) => {
        const id = (item.paymentMode || item.code || item.channel || "")
          .toString()
          .toLowerCase()
          .trim();
        initialCredits[id] = Number(item.bankCredits || 0);
      });
      setBankCredits(initialCredits);
    }
  }, [systemReceiptsData]);

  const handleRangeChange = (range: string) => {
    setActiveRange(range);
    const today = new Date();
    let from = "";
    let to = "";

    switch (range) {
      case "Today":
        from = format(today, "yyyy-MM-dd");
        to = format(today, "yyyy-MM-dd");
        break;
      case "Yesterday":
        const yesterday = subDays(today, 1);
        from = format(yesterday, "yyyy-MM-dd");
        to = format(yesterday, "yyyy-MM-dd");
        break;
      case "This Week":
        from = format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd");
        to = format(today, "yyyy-MM-dd");
        break;
      case "This Month":
        from = format(startOfMonth(today), "yyyy-MM-dd");
        to = format(today, "yyyy-MM-dd");
        break;
      case "Custom Range":
        return;
    }

    if (from && to) {
      form.setValue("fromDate", from, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      form.setValue("toDate", to, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const onSubmit = (data: any) => {
    const paymentModeCredits = tableData.map((row) => {
      const bankCreditAmount = bankCredits[row.id];
      const systemReceiptAmount = row.systemReceipts;
      return {
        payment_mode: row.channel,
        bank_credit_amount: Number(Number(bankCreditAmount).toFixed(2)),
        system_receipt_amount: Number(Number(systemReceiptAmount).toFixed(2)),
        variance: Number((bankCreditAmount - systemReceiptAmount).toFixed(2)),
      };
    });

    const systemTotal = tableData.reduce(
      (sum, row) => sum + row.systemReceipts,
      0,
    );
    const bankTotal = tableData.reduce(
      (sum, row) => sum + (bankCredits[row.id] || 0),
      0,
    );
    const variance = bankTotal - systemTotal;

    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      bank_account_id: Number(data.sourceBank),
      period_from:
        typeof data.fromDate === "string"
          ? data.fromDate
          : format(new Date(data.fromDate), "yyyy-MM-dd"),
      period_to:
        typeof data.toDate === "string"
          ? data.toDate
          : format(new Date(data.toDate), "yyyy-MM-dd"),
      input_mode: selectedFile ? "file_upload" : "manual_entry",
      payment_mode_credits: paymentModeCredits,
      system_total: systemTotal,
      bank_total: bankTotal,
      variance: variance,
    };

    startReconciliation(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const [bankCredits, setBankCredits] = useState<Record<string, number>>({});

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!isOpen) {
      const todayDate = format(new Date(), "yyyy-MM-dd");
      form.reset({
        fromDate: todayDate,
        toDate: todayDate,
        sourceBank: "",
      });
      setActiveRange("Today");
      setSelectedFile(null);
      setUploadProgress(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      const channelTable = systemReceiptsData?.result?.channelTable;
      if (Array.isArray(channelTable) && channelTable.length > 0) {
        const initialCredits: Record<string, number> = {};
        channelTable.forEach((item: any) => {
          const id = (item.paymentMode || item.code || item.channel || "")
            .toString()
            .toLowerCase()
            .trim();
          initialCredits[id] = Number(item.bankCredits || 0);
        });
        setBankCredits(initialCredits);
      } else {
        setBankCredits({});
      }
    }
  }, [isOpen, form, systemReceiptsData]);

  const processFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "File size exceeds the 10MB limit.",
        variant: "destructive",
      });
      return;
    }

    const fileType = file.name.split(".").pop()?.toLowerCase();
    if (!["csv", "xls", "xlsx"].includes(fileType || "")) {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV or Excel file (.csv, .xls, .xlsx).",
        variant: "destructive",
      });
      return;
    }

    const sourceBankId = form.getValues("sourceBank");
    if (!sourceBankId) {
      toast({
        title: "Missing Bank Account",
        description:
          "Please select a Source Bank Account above before uploading.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    setUploadProgress(0);

    uploadBankStatement.mutate(
      {
        file,
        remoteUtilityId: Number(remoteUtilityId),
        bankAccountId: Number(sourceBankId),
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      },
      {
        onSuccess: (data) => {
          setUploadProgress(null);

          if (
            data?.result?.bankCredits &&
            Array.isArray(data.result.bankCredits)
          ) {
            const uploadedCredits: Record<string, number> = {};
            data.result.bankCredits.forEach((item: any) => {
              const id = (item.paymentMode || "")
                .toString()
                .toLowerCase()
                .trim();
              uploadedCredits[id] = Number(item.bankCreditAmount || 0);

              // Map Cheque to check to handle UI mismatch
              if (id === "cheque") {
                uploadedCredits["check"] = Number(item.bankCreditAmount || 0);
              }
            });
            setBankCredits((prev) => ({ ...prev, ...uploadedCredits }));
            toast({
              title: "Statement Uploaded Successfully",
              description: `Parsed ${file.name} and loaded statement credits.`,
            });
          } else {
            toast({
              title: "Statement Uploaded Successfully",
              description: "No bank credits data found in the response.",
            });
          }
        },
        onError: () => {
          setUploadProgress(null);
          setSelectedFile(null);
        },
      },
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Reset to default credits from systemReceiptsData
    const channelTable = systemReceiptsData?.result?.channelTable;
    if (Array.isArray(channelTable) && channelTable.length > 0) {
      const initialCredits: Record<string, number> = {};
      channelTable.forEach((item: any) => {
        const id = (item.paymentMode || item.code || item.channel || "")
          .toString()
          .toLowerCase()
          .trim();
        initialCredits[id] = Number(item.bankCredits || 0);
      });
      setBankCredits(initialCredits);
    } else {
      setBankCredits({});
    }
    toast({
      title: "File Removed",
      description: "Statement data cleared.",
    });
  };

  const handleBankCreditChange = (id: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setBankCredits((prev) => ({ ...prev, [id]: numValue }));
  };

  const totalSystem = tableData.reduce(
    (sum, row) => sum + row.systemReceipts,
    0,
  );
  const totalBank = tableData.reduce(
    (sum, row) => sum + (bankCredits[row.id] || 0),
    0,
  );
  const netVariance = totalBank - totalSystem;
  const channelsWithVariance = tableData.filter(
    (row) => (bankCredits[row.id] || 0) - row.systemReceipts !== 0,
  ).length;

  const dataWithTotals = [
    ...tableData,
    {
      id: "total",
      channel: "Totals",
      systemReceipts: totalSystem,
    },
  ];

  const columns: TableColumn<any>[] = [
    {
      key: "channel",
      header: "Payment Mode",
      type: "custom",
      render: (value: string, row: any) => (
        <span
          className={
            row.id === "total"
              ? "font-bold text-gray-500"
              : "font-bold text-black"
          }
        >
          {value}
        </span>
      ),
    },
    {
      key: "systemReceipts",
      header: `System Receipts (${currency})`,
      render: (value: number, row: any) => (
        <span
          className={
            row.id === "total"
              ? "font-bold text-gray-900"
              : "font-medium text-gray-600"
          }
        >
          {value}
        </span>
      ),
    },
    {
      key: "bankCredits",
      header: `Bank Credits (${currency}) *`,
      render: (_: any, row: any) => {
        if (row.id === "total") {
          return (
            <span className="font-bold text-gray-900 ml-[13px]">
              {totalBank.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          );
        }
        return (
          <Input
            type="number"
            value={bankCredits[row.id] ?? ""}
            onChange={(e) => handleBankCreditChange(row.id, e.target.value)}
            className="w-32 text-right"
          />
        );
      },
    },
    {
      key: "variance",
      header: `Variance (${currency})`,
      render: (_: any, row: any) => {
        let variance = 0;
        if (row.id === "total") {
          variance = netVariance;
        } else {
          variance = (bankCredits[row.id] || 0) - row.systemReceipts;
        }
        const isPositive = variance > 0;
        const isZero = variance === 0;

        return (
          <span
            className={`font-bold ${
              isZero
                ? row.id === "total"
                  ? "text-gray-900"
                  : "text-green-600"
                : isPositive
                  ? "text-orange-500"
                  : "text-red-500"
            }`}
          >
            {isPositive ? "+" : ""}
            {variance.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      type: "custom",
      render: (_: any, row: any) => {
        if (row.id === "total") return null;
        return (
          <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400">
            <X className="h-4 w-4" />
          </Button>
        );
      },
    },
  ];

  const dashboardCard = (
    <div className="border border-gray-200 rounded-lg p-4 flex items-center gap-10 bg-white shadow-sm">
      <div className="flex flex-col">
        <span className="text-[12px] font-bold text-gray-500 tracking-wider mb-1">
          System Total
        </span>
        <span className="text-lg font-bold text-gray-900">
          {currency} {totalSystem}
        </span>
      </div>
      <div className="flex flex-col">
        <span className="text-[12px] font-bold text-gray-500 tracking-wider mb-1">
          Bank Total
        </span>
        <span className="text-lg font-bold text-gray-900">
          {currency} {totalBank}
        </span>
      </div>
      <div className="flex flex-col">
        <span className="text-[12px] font-bold text-gray-500 tracking-wider mb-1">
          Net Variance
        </span>
        <span
          className={`text-lg font-bold ${
            netVariance > 0
              ? "text-orange-500"
              : netVariance < 0
                ? "text-red-500"
                : "text-gray-900"
          }`}
        >
          {netVariance > 0 ? "+" : ""}
          {currency}{" "}
          {netVariance.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </div>
      <div className="flex flex-col">
        <span className="text-[12px] font-bold text-gray-500 tracking-wider mb-1">
          Channels with Variance
        </span>
        <span className="text-lg font-bold text-gray-900">
          {channelsWithVariance} channel{channelsWithVariance !== 1 ? "s" : ""}
        </span>
      </div>
    </div>
  );

  const tabComponents = {
    upload: {
      label: "Upload File",
      component: (
        <div className="mt-4 flex flex-col gap-4">
          {!selectedFile ? (
            <div
              className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg bg-gray-50 transition-all cursor-pointer ${
                isDragActive
                  ? "border-blue-500 bg-blue-50/50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={handleBrowseClick}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv,.xls,.xlsx"
                className="hidden"
              />
              <div className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm mb-3">
                <Upload className="w-6 h-6 text-gray-500" />
              </div>
              <p className="text-sm font-semibold text-gray-900">
                Drag and drop your bank statement here
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Supports CSV, XLS, XLSX up to 10MB
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 border-gray-200 hover:bg-white bg-white font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowseClick();
                }}
              >
                Browse Files
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4 p-4 border border-gray-100 bg-white rounded-xl shadow-sm">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                <FileSpreadsheet className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {selectedFile.name}
                  </p>
                  {uploadProgress !== null ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                      <Loader2 className="w-3 h-3 animate-spin" /> Uploading{" "}
                      {uploadProgress}%
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-100">
                      <CheckCircle2 className="w-3 h-3" /> Ready
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
                {uploadProgress !== null && (
                  <Progress value={uploadProgress} className="h-1.5 mt-2" />
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                onClick={handleRemoveFile}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
          <GenericTable
            data={dataWithTotals}
            columns={columns as any}
            rowKey="id"
            getRowClassName={(row: any) =>
              row.id === "total" ? "bg-gray-50/80 border-t border-gray-100" : ""
            }
          />
          {dashboardCard}
        </div>
      ),
    },
    manual: {
      label: "Enter Manually",
      component: (
        <div className="mt-4 flex flex-col gap-4">
          <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Type the bank credit amount for each payment mode from the bank
              statement. System receipts are pre-filled for reference. Leave a
              field at <strong>0</strong> if the channel had no activity.
            </p>
          </div>
          <GenericTable
            data={dataWithTotals}
            columns={columns as any}
            rowKey="id"
            getRowClassName={(row: any) =>
              row.id === "total" ? "bg-gray-50/80 border-t border-gray-100" : ""
            }
          />
          {dashboardCard}
        </div>
      ),
    },
  };

  const allFields: FormField[] = [
    {
      name: "step1Header",
      type: "custom",
      className: "col-span-2",
      customRender: () => (
        <div className="flex flex-col gap-4 mb-2">
          <h3 className="text-xs font-bold text-gray-500 flex items-center gap-2">
            Step 1 — Reconciliation Period
          </h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleRangeChange("Today")}
              variant={activeRange === "Today" ? "default" : "outline"}
              className={`rounded-full ${
                activeRange === "Today"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              }`}
            >
              Today
            </Button>
            <Button
              size="sm"
              onClick={() => handleRangeChange("Yesterday")}
              variant={activeRange === "Yesterday" ? "default" : "outline"}
              className={`rounded-full ${
                activeRange === "Yesterday"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              }`}
            >
              Yesterday
            </Button>
            <Button
              size="sm"
              onClick={() => handleRangeChange("This Week")}
              variant={activeRange === "This Week" ? "default" : "outline"}
              className={`rounded-full ${
                activeRange === "This Week"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              }`}
            >
              This Week
            </Button>
            <Button
              size="sm"
              onClick={() => handleRangeChange("This Month")}
              variant={activeRange === "This Month" ? "default" : "outline"}
              className={`rounded-full ${
                activeRange === "This Month"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              }`}
            >
              This Month
            </Button>
            <Button
              size="sm"
              onClick={() => handleRangeChange("Custom Range")}
              variant={activeRange === "Custom Range" ? "default" : "outline"}
              className={`rounded-full ${
                activeRange === "Custom Range"
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : ""
              }`}
            >
              Custom Range
            </Button>
          </div>
        </div>
      ),
    },
    {
      name: "fromDate",
      label: "From Date",
      type: "datepicker",
      required: true,
    },
    {
      name: "toDate",
      label: "To Date",
      type: "datepicker",
      required: true,
    },
    {
      name: "sourceBank",
      label: "Source Bank",
      type: "select",
      required: true,
      options: sourceBankOptions,
      className: "col-span-2",
    },
    {
      name: "step2Content",
      type: "custom",
      className: "col-span-2 mt-4",
      customRender: () => (
        <div className="flex flex-col gap-4">
          <h3 className="text-xs font-bold text-gray-500 flex items-center gap-2">
            Step 2 — Bank Credits (As Of Statement Date)
          </h3>
          <Tabs
            defaultValue="upload"
            className="w-full"
            disableUrlSync
            tabComponents={tabComponents}
          />
        </div>
      ),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 border-0">
        <DialogHeader className="p-4 border-b border-gray-100">
          <DialogTitle className="flex items-center gap-2 text-lg font-bold">
            <Play className="w-5 h-5 text-gray-600" />
            Start Reconciliation
          </DialogTitle>
        </DialogHeader>

        <div className="pl-6 pr-6">
          <DynamicForm
            form={form}
            fields={allFields as any}
            gridColumns={2}
            config={{ showSubmitButton: false, classes: FIELD_CLASSES }}
          />
        </div>

        <DialogFooter className="p-4 border-t border-gray-100 flex items-center justify-between w-full bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 flex-1">
            All entered bank credits will be saved to the reconciliation ledger
            for this period.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="px-6">
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isStartingReconciliation}
            >
              {isStartingReconciliation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reconciling...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Reconciliation
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
